import { useEffect, useRef } from 'react';

export interface PointerState {
  x: number;
  y: number;
  // Whether the pointer is currently inside the window.
  active: boolean;
}

/**
 * A single, shared pointer tracker. Returns a ref (not state) so consumers
 * can read the latest position inside a requestAnimationFrame loop WITHOUT
 * triggering React re-renders on every mouse move.
 *
 * This replaces the four separate `mousemove` listeners that used to live in
 * MeshGradient, GlowingOrbs, MouseFollower and ConstellationField.
 */
export const usePointer = () => {
  const pointer = useRef<PointerState>({
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
    active: false,
  });

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointer.current.x = e.clientX;
      pointer.current.y = e.clientY;
      pointer.current.active = true;
    };
    const onLeave = () => {
      pointer.current.active = false;
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return pointer;
};
